import { useEffect, useMemo, useState } from "react";
import { MagnifyingGlassIcon, XIcon, CircleNotchIcon } from "@phosphor-icons/react";

/**
 * Búsqueda mientras se escribe, sobre los registros que el usuario ya puede ver (respeta sus permisos).
 *  · Sin mayúsculas ni tildes; todas las palabras escritas deben aparecer en algún dato del registro.
 *  · Filtra en el mismo listado (sin lista desplegable ni botón «Buscar»); al borrar vuelve la vista inicial.
 *  · Como filtra en memoria lo ya cargado, una búsqueda anterior nunca reemplaza a la última escrita.
 */
export const normalizar = (t) => String(t ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export function useLiveSearch(items, campos, { espera = 120, estado } = {}) {
  const interno = useState("");
  const [texto, setTexto] = estado || interno;      // estado externo opcional (p. ej. usePageState para conservar la búsqueda)
  const [aplicado, setAplicado] = useState(texto);
  useEffect(() => {
    if (texto === aplicado) return undefined;
    const t = setTimeout(() => setAplicado(texto), espera);
    return () => clearTimeout(t);
  }, [texto, aplicado, espera]);
  const indice = useMemo(() => (items || []).map((it) => [it, normalizar((campos(it) || []).filter((v) => v !== null && v !== undefined).join(" "))]),
    [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const palabras = useMemo(() => normalizar(aplicado).split(/\s+/).filter(Boolean), [aplicado]);
  const resultados = useMemo(() => (palabras.length ? indice.filter(([, t]) => palabras.every((w) => t.includes(w))).map(([it]) => it) : (items || [])),
    [indice, palabras, items]);
  return { texto, setTexto, resultados, activa: palabras.length > 0, buscando: texto !== aplicado, total: (items || []).length };
}

/** Campo de búsqueda con etiqueta accesible, indicador, cantidad de coincidencias y botón para limpiar. */
export function SearchBox({ busqueda, placeholder = "Buscar…", testid = "buscar", label = "Buscar", className = "" }) {
  const { texto, setTexto, resultados, activa, buscando, total } = busqueda;
  return (
    <div className={`flex flex-wrap items-center gap-2 flex-1 min-w-[240px] ${className}`}>
      <label className="relative flex-1 min-w-[220px]">
        <span className="sr-only">{label}</span>
        <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" aria-hidden="true" />
        <input type="search" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder={placeholder} aria-label={label}
          data-testid={testid} autoComplete="off"
          className="w-full min-h-[40px] bg-input-bg border border-input rounded-md pl-9 pr-9 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-focus/70 focus:border-focus" />
        {texto && (
          <button type="button" onClick={() => setTexto("")} aria-label="Limpiar búsqueda" data-testid={`${testid}-limpiar`}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded text-text-muted hover:text-text-primary">
            <XIcon size={14} />
          </button>
        )}
      </label>
      <span className="text-xs text-text-muted whitespace-nowrap flex items-center gap-1" aria-live="polite" data-testid={`${testid}-conteo`}>
        {buscando ? <><CircleNotchIcon size={12} className="animate-spin" /> Buscando…</>
          : activa ? (resultados.length ? `${resultados.length} de ${total} coincidencias` : "Sin resultados") : `${total} registros`}
      </span>
    </div>
  );
}
